import './_services-rates.scss';
import './_services-rates.dark-mode.scss';
import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory, Redirect } from 'react-router-dom';
import { isBoolean, isEmpty, isEqual } from 'lodash';

import RouteLeavingGuard from '../../../components/Modal/RouteLeavingGuard';
import Alert from '../../../components/Alert';
import Body from './components/Body';
import TableLoader from '../Main/components/Listing/Table/Loader';
import Loader from '../../../components/Loaders/Loader';

import { getNumberValue, pricePrecision } from './helpers';
import { getErrorMessage } from '../Main/helpers/getters';
import { serviceGetAccessRates, serviceMassEditRates } from '../../../services/endpoints/services';

const ServicesRates = () => {
  const history = useHistory();

  const { servicesChecked, config } = useSelector((state) => state.services);

  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [initList, setInitList] = useState([]);
  const [formData, setFormData] = useState([]);
  const [percent, setPercent] = useState('');

  const list = useMemo(
    () => initList.reduce((acc, category) => {
      if (category.services.some((service) => servicesChecked.includes(service.id))) {
        const services = category.services.filter((service) => servicesChecked.includes(service.id));
        return [
          ...acc,
          { name: category.title, visibility: category.visibility, type: 'category' },
          ...services.map((service) => ({ ...service, visibility: !category.visibility ? false : !!service.status })),
        ];
      }

      return acc;
    }, []),
    [initList, servicesChecked],
  );

  const getCategoriesList = async (ids) => {
    setIsLoading(true);

    try {
      const response = await serviceGetAccessRates(ids);
      setInitList(response?.data?.services);
      setIsLoading(false);
    } catch (error) {
      history.push('/services');
    }
  };

  const onChangePercent = (e) => {
    setIsDirty(true);
    const { value } = e.target;
    return getNumberValue(value);
  };

  const onChangeMassPercent = (e) => {
    const percentValue = onChangePercent(e);
    if (!isBoolean(percentValue)) {
      setPercent(percentValue);

      setFormData((prevState) => prevState.map((item) => {
        if (!item?.can_edit_mass_rates || !item?.visibility || item?.provider_error) {
          return item;
        }
        if (item.provider_type === 1 && item.auto_rate) {
          return {
            ...item,
            auto_rate_percent: String(percentValue),
            rate: {
              ...item.rate,
              custom_float: pricePrecision(percentValue, item, config),
            },
          };
        }

        return item;
      }));
    }
  };

  const onChangeServicePercent = (e, id) => {
    if (e.target.disabled) {
      return;
    }
    const percentValue = onChangePercent(e);
    if (!isBoolean(percentValue)) {
      setFormData((prevState) => prevState.map((item) => {
        if (!item?.can_edit_mass_rates || !item?.visibility || item?.provider_error) {
          return item;
        }
        if (id === item.id) {
          return {
            ...item,
            auto_rate_percent: String(percentValue),
            rate: {
              ...item.rate,
              custom_float: pricePrecision(percentValue, item, config),
            },
          };
        }
        return item;
      }));
    }
  };

  const changeRateState = (value, id) => {
    setFormData((prevState) => prevState.map((item) => {
      if (!item?.can_edit_mass_rates || !item?.visibility || item?.provider_error) {
        return item;
      }
      if (id === item.id) {
        return {
          ...item,
          rate: {
            ...item.rate,
            custom_float: value,
          },
        };
      }
      return item;
    }));
  };

  const onChangeRate = (e, id) => {
    if (e.target.disabled) {
      return;
    }
    setIsDirty(true);
    const value = e.target.value.replace(/[,,]/, '.');
    if (value === '' || value === '00') {
      changeRateState('0', id);
    } else if (/^0[0-9].*$/.test(value)) {
      const newValue = value.replace(/^0+/, '');
      changeRateState(newValue, id);
    } else {
      const newValue = value.replace(',', '.');
      if (/^[0-9]*(?:\.|\.[0-9]{0,4})?$/.test(newValue)) {
        changeRateState(newValue, id);
      }
    }
  };

  const prepareSubmitForm = () => {
    const result = [];

    formData.forEach((service) => {
      if (!service?.can_edit_mass_rates || service?.provider_error || service?.provider_error) {
        return;
      }
      const findServiceInitial = list.find((item) => item.id === service.id);
      if (service.provider_type === 1 && service.auto_rate) {
        const equal = isEqual(service?.auto_rate_percent, findServiceInitial?.auto_rate_percent);
        if (!equal) {
          result.push({
            service_id: service.id,
            percent: service.auto_rate_percent,
          });
        }
      } else {
        const equal = isEqual(service?.rate?.custom_float, findServiceInitial?.rate?.custom_float);
        if (!equal) {
          result.push({
            service_id: service.id,
            rate: service.rate.custom_float,
          });
        }
      }
    });

    return result;
  };

  const onSubmit = async () => {
    setIsLoadingTable(true);
    setErrorMessage('');

    try {
      const result = prepareSubmitForm();

      if (isEmpty(result)) {
        setIsDirty(false);
        setIsLoadingTable(false);
        return history.push('/services', { clearSelected: true });
      }

      const response = await serviceMassEditRates(result);
      if (response.success) {
        setIsDirty(false);
        setIsLoadingTable(false);
        history.push('/services', { clearSelected: true });
      }
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setIsLoadingTable(false);
    }
  };

  useEffect(() => {
    document.title = 'Services';
    if (servicesChecked?.length) {
      getCategoriesList(servicesChecked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFormData(list?.filter((item) => item.type !== 'category'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initList]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = 'Unsaved changes';
      return 'Unsaved changes';
    };

    isDirty && window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  if (!servicesChecked?.length) {
    return <Redirect to="/services" />;
  }

  return (
    <Grid fluid>
      <RouteLeavingGuard
        when={isDirty}
        navigate={(path) => history.push(path)}
        shouldBlockNavigation={() => {
          if (isDirty) {
            return true;
          }
          return false;
        }}
      />

      {isLoading ? (
        <Loader />
      ) : (
        <div className="services-rates">
          {isLoadingTable ? <TableLoader /> : null}
          <div className="services-rates__header">
            <div className="services-rates__header-info">
              <span className="title">Bulk edit rates</span>
              <div className="services-rates__raise">
                <label htmlFor="import_fixed" className="services-rates__raise-label">
                  Percent, %
                </label>
                <input
                  id="mass_percent"
                  type="text"
                  name="mass_percent"
                  className="form-control"
                  placeholder="0"
                  value={percent || ''}
                  onChange={onChangeMassPercent}
                />
              </div>
            </div>
            {errorMessage && <Alert text={errorMessage} />}
          </div>
          <Body config={config} services={list} formData={formData} onChangeRate={onChangeRate} onChangePercent={onChangeServicePercent} />
          <div className="services-rates__footer">
            <Button onClick={onSubmit} bsStyle="primary" type="submit" className="mr-12">
              Save changes
            </Button>
            <Button onClick={() => history.push('/services')}>Cancel</Button>
          </div>
        </div>
      )}
    </Grid>
  );
};

export default ServicesRates;
